import type { RankEntry } from '../../types';

interface Props {
  financialRanking: RankEntry[];
  emotionalRanking: RankEntry[];
  combinedRanking: RankEntry[];
  decisionConfidence: number;
  financialWeight: number;
  emotionalWeight: number;
}

function fmt$(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function confidenceLevel(margin: number): { label: string; color: string; dot: string } {
  if (margin >= 10) return { label: 'High', color: 'var(--tco-accent)', dot: 'var(--tco-accent)' };
  if (margin >= 3) return { label: 'Medium', color: 'var(--tco-ink-2)', dot: 'var(--tco-ink-2)' };
  return { label: 'Low', color: 'var(--tco-neg)', dot: 'var(--tco-neg)' };
}

function buildExplanation(
  combinedWinnerId: string | undefined,
  financialWinnerId: string | undefined,
  emotionalWinnerId: string | undefined,
  combinedWinnerLabel: string,
  financialWinnerLabel: string,
  emotionalWinnerLabel: string,
): string {
  if (!combinedWinnerId) return '';

  const combinedIsFinancial = combinedWinnerId === financialWinnerId;
  const combinedIsEmotional = combinedWinnerId === emotionalWinnerId;
  const financialIsEmotional = financialWinnerId === emotionalWinnerId;

  if (financialIsEmotional) {
    return `${combinedWinnerLabel} is both the most economical and most satisfying choice.`;
  }

  if (combinedIsFinancial && combinedIsEmotional) {
    return `${combinedWinnerLabel} is the top choice on both cost and satisfaction.`;
  }

  if (combinedIsFinancial && !combinedIsEmotional) {
    return `${combinedWinnerLabel} wins on total cost. Although ${emotionalWinnerLabel} scores higher on satisfaction, the cost difference outweighs the emotional advantage at your current weighting.`;
  }

  if (combinedIsEmotional && !combinedIsFinancial) {
    return `${combinedWinnerLabel} costs more than ${financialWinnerLabel} but scores significantly higher on satisfaction. At your current weighting the emotional advantage justifies the premium.`;
  }

  // Fallback: combined winner is neither financial nor emotional winner
  return `${combinedWinnerLabel} strikes the best balance between cost and satisfaction at your current weighting.`;
}

export default function DecisionSummaryPanel({
  financialRanking,
  emotionalRanking,
  combinedRanking,
  decisionConfidence,
  financialWeight,
  emotionalWeight,
}: Props) {
  const financialWinner = financialRanking.find((e) => e.rank === 1);
  const emotionalWinner = emotionalRanking.find((e) => e.rank === 1);
  const combinedWinner = combinedRanking.find((e) => e.rank === 1);

  const explanation = buildExplanation(
    combinedWinner?.vehicle_id,
    financialWinner?.vehicle_id,
    emotionalWinner?.vehicle_id,
    combinedWinner?.label ?? '',
    financialWinner?.label ?? '',
    emotionalWinner?.label ?? '',
  );

  const conf = confidenceLevel(decisionConfidence);
  const financialPct = Math.round(financialWeight * 100);
  const emotionalPct = Math.round(emotionalWeight * 100);

  // Plain English trade-off sentence: financial rank 1 vs rank 2
  const fin1 = financialRanking.find((e) => e.rank === 1);
  const fin2 = financialRanking.find((e) => e.rank === 2);
  let tradeOffSentence: string | null = null;
  if (fin1 && fin2) {
    const emo1 = emotionalRanking.find((e) => e.vehicle_id === fin1.vehicle_id);
    const emo2 = emotionalRanking.find((e) => e.vehicle_id === fin2.vehicle_id);
    const tcoGap = fin2.score - fin1.score;
    const emoGap = emo1 && emo2 ? (emo2.score - emo1.score) * 100 : null;
    if (tcoGap > 0 && emoGap !== null && emoGap > 0.05) {
      tradeOffSentence = `The ${fin2.label} costs ${fmt$(tcoGap)} more and scores ${emoGap.toFixed(1)} points higher on satisfaction.`;
    }
  }

  const eyebrowStyle = {
    color: 'var(--tco-ink-3)',
    fontFamily: 'var(--tco-font-sans)',
    letterSpacing: '0.18em',
  };

  return (
    <div
      className="rounded-xl p-5 space-y-5"
      style={{ background: 'var(--tco-surface)', border: '1px solid var(--tco-line)' }}
    >
      <p className="text-xs font-semibold uppercase" style={eyebrowStyle}>
        Decision Summary
      </p>

      {/* Winner cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Financial Winner */}
        <div
          className="rounded-lg px-4 py-3 flex flex-col gap-1"
          style={{ background: 'var(--tco-line-soft)', border: '1px solid var(--tco-line)' }}
        >
          <p className="text-xs uppercase" style={eyebrowStyle}>Financial Winner</p>
          <p className="font-semibold text-sm" style={{ color: 'var(--tco-ink)' }}>{financialWinner?.label ?? '—'}</p>
          <p className="text-xs font-mono" style={{ color: 'var(--tco-ink-2)' }}>
            {financialWinner ? fmt$(financialWinner.score) : ''}
          </p>
        </div>

        {/* Emotional Winner */}
        <div
          className="rounded-lg px-4 py-3 flex flex-col gap-1"
          style={{ background: 'var(--tco-line-soft)', border: '1px solid var(--tco-line)' }}
        >
          <p className="text-xs uppercase" style={eyebrowStyle}>Emotional Winner</p>
          <p className="font-semibold text-sm" style={{ color: 'var(--tco-ink)' }}>{emotionalWinner?.label ?? '—'}</p>
          <p className="text-xs font-mono" style={{ color: 'var(--tco-ink-2)' }}>
            {emotionalWinner ? `Score ${(emotionalWinner.score * 100).toFixed(1)} / 100` : ''}
          </p>
        </div>

        {/* Combined Winner — highlighted with emerald accent stripe */}
        <div
          className="rounded-lg px-4 py-3 flex flex-col gap-1"
          style={{
            background: 'var(--tco-accent-soft)',
            border: '1px solid var(--tco-accent)',
            borderLeft: '3px solid var(--tco-accent)',
          }}
        >
          <p className="text-xs uppercase font-semibold" style={{ ...eyebrowStyle, color: 'var(--tco-accent)' }}>
            Combined Winner
          </p>
          <p className="font-bold text-sm" style={{ color: 'var(--tco-ink)' }}>{combinedWinner?.label ?? '—'}</p>
          <p className="text-xs font-mono" style={{ color: 'var(--tco-ink-2)' }}>
            {combinedWinner ? `Score ${(combinedWinner.score * 100).toFixed(1)} / 100` : ''}
          </p>
        </div>
      </div>

      {/* Plain English explanation */}
      {explanation && (
        <p
          className="text-sm leading-relaxed rounded-lg px-4 py-3"
          style={{
            color: 'var(--tco-ink)',
            background: 'var(--tco-line-soft)',
            border: '1px solid var(--tco-line)',
          }}
        >
          {explanation}
        </p>
      )}

      {/* Trade-off sentence: cost gap vs satisfaction gap between rank 1 and rank 2 */}
      {tradeOffSentence && (
        <p className="text-sm leading-relaxed" style={{ color: 'var(--tco-ink-2)' }}>
          {tradeOffSentence}
        </p>
      )}

      {/* Weighting + confidence */}
      <div
        className="flex flex-wrap items-start gap-4 text-xs pt-4"
        style={{ color: 'var(--tco-ink-2)', borderTop: '1px solid var(--tco-line)' }}
      >
        {/* Weighting */}
        <div className="flex flex-col gap-0.5">
          <p className="uppercase text-xs" style={eyebrowStyle}>Decision Weighting</p>
          <p className="font-medium" style={{ color: 'var(--tco-ink)' }}>
            {financialPct}% Financial, {emotionalPct}% Emotional
          </p>
        </div>

        {/* Decision Confidence */}
        <div className="flex flex-col gap-0.5 ml-auto shrink-0">
          <p className="uppercase text-xs" style={eyebrowStyle}>Decision Confidence</p>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold" style={{ color: 'var(--tco-ink)' }}>{decisionConfidence.toFixed(1)}%</span>
            <span className="text-xs" style={{ color: 'var(--tco-ink-2)' }}>margin</span>
            <span className="w-2 h-2 rounded-full" style={{ background: conf.dot }} />
            <span className="text-xs font-semibold" style={{ color: conf.color }}>{conf.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
